/**
 * Generic GoJS diagram primitives and helpers shared by the DiagramGraphComponent
 * and the feature-level graphs that build on it (e.g. the company ownership graph).
 *
 * This module is intentionally free of any company/ownership domain types so the
 * shared diagram layer and the feature layer can both depend on it without a cycle.
 */

import { Diagram, GraphObject, ImageRendererOptions, LayeredDigraphLayout, Link, Node, Size } from 'gojs';

export type ExpandNodeDirection = 'up' | 'down';

export type DiagramLinkStyle = 'bezier' | 'orthogonal';

export type DiagramDirection = 'top_down' | 'left_right';

export interface DiagramOptions {
  direction: DiagramDirection;
  layerSpacing: number;
  columnSpacing: number;
}

export const DEFAULT_DIAGRAM_OPTIONS: DiagramOptions = {
  direction: 'top_down',
  layerSpacing: 140,
  columnSpacing: 20,
} as const;

export const DIAGRAM_DIRECTION_ANGLES: Record<DiagramDirection, number> = {
  top_down: 270,
  left_right: 180,
};

export interface MinMaxRange {
  min: number;
  max: number;
}

export const DEFAULT_MIN_NODE_SCALE = 1;
export const DEFAULT_MAX_NODE_SCALE = 5;

export function scaleNumber(
  inputNumber: number,
  givenRange: MinMaxRange,
  desiredRange: MinMaxRange = {
    min: DEFAULT_MIN_NODE_SCALE,
    max: DEFAULT_MAX_NODE_SCALE,
  },
): number {
  const number = Math.abs(typeof inputNumber === 'number' ? inputNumber : 0);
  const percent = (number - givenRange.min) / (givenRange.max - givenRange.min);
  const outputNumber = percent * (desiredRange.max - desiredRange.min) + desiredRange.min;
  return outputNumber;
}

/**
 * Minimal node-data contract the generic DiagramGraphComponent depends on.
 * Feature-specific node data (e.g. the ownership graph's NodeData) extends this
 * with its own domain fields, which only the injected templates need to know about.
 */
export interface DiagramNodeData {
  key: string;
  visible?: boolean;
  isTopNode?: boolean;
  isHovered?: boolean;
  isCollapsedFrom?: boolean;
  isCollapsedTo?: boolean;
}

/**
 * Generic operations the DiagramGraphComponent exposes to the injected node
 * template. The template decides *when* to call them using its own domain
 * knowledge (e.g. whether a node's parents have been fetched yet).
 */
export interface DiagramHostApi {
  expandUp: (data: DiagramNodeData) => void;
  expandDown: (data: DiagramNodeData) => void;
  toggleCollapseUp: (node: Node) => void;
  toggleCollapseDown: (node: Node) => void;
  setHovered: (obj: GraphObject, hovered: boolean) => void;
}

/**
 * Template factories injected into DiagramGraphComponent so it can render a
 * feature-specific diagram without knowing anything about the domain.
 */
export interface DiagramGraphConfig {
  makeNodeTemplate: ($make: typeof GraphObject.make, host: DiagramHostApi) => Node;
  makeLinkTemplate: ($make: typeof GraphObject.make) => Link;
}

/**
 * Max image size is restricted because on bigger sizes GoJS can fail to export it
 * (there is a browser restriction on blob size)
 */
export const DEFAULT_MAX_IMAGE_SIZE = 10000;

export function getImageMaxSizeOptions<T extends { width: number; height: number }>(
  item: T,
  maxImageSize: number,
): ImageRendererOptions {
  if (!item || !maxImageSize) {
    return {
      scale: 1,
    };
  }
  // if maxImageSize equals width or height we return a real size and scale
  if (item.width === maxImageSize || item.height === maxImageSize) {
    return {
      scale: 1,
      maxSize: new Size(item.width, item.height),
    };
  }
  // if not we try to scale the image
  const isHeightBiggerThanWidth = item.height > item.width;
  if (isHeightBiggerThanWidth) {
    const scaledHeight = scaleNumber(
      item.height,
      {
        min: 0,
        max: item.height,
      },
      {
        min: 0,
        max: maxImageSize,
      },
    );
    const scaledScale = scaledHeight / item.height;
    return {
      scale: scaledScale,
      maxSize: new Size(Infinity, scaledHeight),
    };
  } else {
    const scaledWidth = scaleNumber(
      item.width,
      {
        min: 0,
        max: item.width,
      },
      {
        min: 0,
        max: maxImageSize,
      },
    );
    const scaledScale = scaledWidth / item.width;
    return {
      scale: scaledScale,
      maxSize: new Size(scaledWidth, Infinity),
    };
  }
}

export function getMaxImageSizes<T extends { width: number; height: number }>(item: T): number[] {
  if (!item || !item.width || !item.height) {
    return [];
  }
  const isHeightBiggerThanWidth = item.height > item.width;
  const steps = 10;
  const maxSize = isHeightBiggerThanWidth ? item.height : item.width;
  return Array.from(new Array(steps), (x, i) => i * (maxSize / steps))
    .concat([maxSize])
    .filter((i) => i)
    .reverse();
}

export function hasImageSrcData(image?: HTMLImageElement): boolean {
  // we assume that src length can not be less than 50
  return (image?.src?.split(',')[1]?.length ?? 0) > 50;
}

export function makeDiagram(
  div: HTMLDivElement,
  $make: typeof GraphObject.make,
  chartOptions: DiagramOptions = DEFAULT_DIAGRAM_OPTIONS,
): Diagram {
  const layout = $make(
    LayeredDigraphLayout, // use a TreeLayout to position all of the nodes
    {
      // properties for most of the tree:
      isInitial: true,
      layerSpacing: chartOptions.layerSpacing,
      columnSpacing: chartOptions.columnSpacing,
      direction: DIAGRAM_DIRECTION_ANGLES[chartOptions.direction],
      packOption: LayeredDigraphLayout.PackAll,
      layeringOption: LayeredDigraphLayout.LayerOptimalLinkLength,
    },
  );
  return $make(Diagram, div, {
    initialAutoScale: Diagram.UniformToFill,
    layout: layout,
  });
}

export function updateSegmentIndexes(diagram: Diagram): void {
  diagram.links.each((l) => {
    l.elt(1).segmentIndex = -Infinity;
  });
  diagram.nodes.each((n) => {
    const outs = n.findLinksOutOf();
    const ins = n.findLinksInto();
    if (outs.count === 1) {
      outs.each((i) => {
        i.elt(1).segmentIndex = 1;
        i.elt(1).segmentFraction = 0.5;
      });
    } else if (ins.count === 1) {
      ins.each((i) => {
        i.elt(1).segmentIndex = -2;
      });
    } else if (outs.count > 1) {
      outs.each((i) => {
        i.elt(1).segmentIndex = 1;
        i.elt(1).segmentFraction = 0.5;
      });
    }
  });
}
